import { styled } from '@mui/material';
import { useViewport } from 'contexts/viewport';
import { forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Accordion, AccordionDetails, AccordionSummary } from 'shell/components/Accordion';
import Box from 'shell/components/Box';
import Button from 'shell/components/Button';
import Divider from 'shell/components/Divider';
import Stack from 'shell/components/Stack';
import Text from 'shell/components/Text';
import ViewMore from 'shell/components/ViewMore';
import { roundTo } from 'utils/helper';

const ViewRubricTable = forwardRef(({ data, isExpanded, ...rest }, ref) => {
    const { device } = useViewport();
    const { t } = useTranslation('assignment');

    const totalMaxPoints = roundTo(
        data
            .map((criterion) => {
                return Math.max(...criterion.options.map((option) => Number(option.rate)));
            })
            .reduce((acc, cur) => acc + cur, 0)
    );

    return (
        <RootStack ref={ref} {...rest}>
            <CustomBox>
                <Text variant={'p1'} weight={'semibold'}>
                    {t('rubric.criteria', 'Criteria')}
                </Text>
            </CustomBox>
            <Stack>
                {data.map((criterion) => {
                    const criterionMaxPoints = Math.max(...criterion.options.map((option) => Number(option.rate)));
                    return (
                        <Stack key={criterion.id}>
                            <Accordion expanded={isExpanded}>
                                <CustomAccordionSummary
                                    component={'div'}
                                    isExpanded={isExpanded}
                                    expandIcon={undefined}
                                    tabIndex={-1}
                                >
                                    <CriterionHeaderStack spacing={2.5}>
                                        <Stack direction={'row'} justifyContent={'space-between'}>
                                            <Text variant={'p2'}>{criterion.title}</Text>
                                            <Text variant={'p2'}>
                                                {t('rubric.criterion-points', 'Pts')}: -/{criterionMaxPoints}
                                            </Text>
                                        </Stack>
                                        <ViewMore TextComponentProps={{ variant: 'p3' }}>
                                            {criterion.description}
                                        </ViewMore>
                                    </CriterionHeaderStack>
                                </CustomAccordionSummary>
                                <CustomAccordionDetails>
                                    <OverflowStack
                                        direction={device === 'mobile' ? 'column' : 'row'}
                                        divider={
                                            <Divider
                                                flexItem
                                                orientation={device === 'mobile' ? 'horizontal' : 'vertical'}
                                            />
                                        }
                                    >
                                        {criterion.options.map((option) => (
                                            <CustomStack key={option.id} spacing={3}>
                                                <Stack direction={'row'} justifyContent={'space-between'}>
                                                    <Stack alignItems={'flex-start'} spacing={3}>
                                                        <Text data-cy={`rubric-points-${option.rate}`} variant={'p2'}>
                                                            {t('rubric.points-value', '{{points}} pts', {
                                                                points: option.rate,
                                                            })}
                                                        </Text>
                                                        <Text variant={'p2'}>{option.title}</Text>
                                                    </Stack>
                                                </Stack>
                                                <Text align={'left'} variant={'p3'}>
                                                    {option.description}
                                                </Text>
                                            </CustomStack>
                                        ))}
                                    </OverflowStack>
                                </CustomAccordionDetails>
                            </Accordion>
                        </Stack>
                    );
                })}
            </Stack>

            <FooterBox>
                <Stack direction={'row'} divider={<Divider flexItem orientation={'vertical'} />}>
                    <CustomText sx={{ flex: 1 }} align={'right'} variant={'p2'}>
                        {t('rubric.total-points-out-of', 'Total Points Out of {{points}}', { points: totalMaxPoints })}
                    </CustomText>
                    <CustomText>-</CustomText>
                </Stack>
            </FooterBox>
        </RootStack>
    );
});

const RootStack = styled(Stack)(({ theme }) => {
    return {
        border: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        borderRadius: '12px',
    };
});

const CustomBox = styled(Box)(({ theme }) => {
    return {
        borderRadius: '12px 12px 0 0',
        padding: theme.spacing(3),
        background: theme.palette.ensightful.primary[50],
    };
});

const CriterionHeaderStack = styled(Stack)(({ theme }) => {
    return {
        borderTop: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        borderBottom: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        padding: theme.spacing(2.5),
        background: theme.palette.ensightful.neutral[100],
    };
});

const OverflowStack = styled(Stack)(({ theme }) => {
    return {
        overflowX: 'auto',
        '&:focus-visible': {
            outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
            outlineOffset: '-5px',
        },
    };
});

const CustomStack = styled(Stack)(({ theme }) => {
    return {
        flex: 1,
        minWidth: '240px',
        minHeight: '100%',
        padding: theme.spacing(3),
    };
});

const FooterBox = styled(Box)(({ theme }) => {
    return {
        borderTop: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        borderRadius: '0 0 12px 12px',
        background: theme.palette.ensightful.primary[50],
    };
});

const CustomText = styled(Text)(({ theme }) => {
    return {
        padding: theme.spacing(4),
    };
});

const CustomAccordionSummary = styled(AccordionSummary)(({ theme, isExpanded }) => {
    return {
        '& > div > div': {
            width: '100%',
            cursor: 'auto',
            borderBottom: isExpanded ? `1px solid ${theme.palette.grey[300]}` : 0,
        },
    };
});

const CustomAccordionDetails = styled(AccordionDetails)(() => {
    return {
        marginTop: 0,
    };
});

const useViewAccordionRubricTableButton = () => {
    const [isExpanded, setIsExpanded] = useState(false);
    const { t } = useTranslation('assignment');

    const handleClick = () => setIsExpanded((prev) => !prev);

    const AccordionRubricTableButton = () => {
        return (
            <Button variant='borderless' onClick={handleClick}>
                {isExpanded
                    ? t('rubric.collapse-rubric', 'Collapse Rubric')
                    : t('rubric.expand-rubric', 'Expand Rubric')}
            </Button>
        );
    };

    return {
        AccordionRubricTableButton,
        isExpanded,
    };
};

export { ViewRubricTable as default, useViewAccordionRubricTableButton };
